import { useIntl } from 'react-intl';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { AccordionDetails, AccordionSummary, Box } from '@material-ui/core';
import React, { FC } from 'react';
import { StyledAccordion, TypographyWrapper } from '../..';
import { FAQ } from '../../../types/shopConfig/shopConfigV2';
import { ShipDeadline } from '../../../util/shipping';
import { CountryCode } from '../../../types/apollo/generated/types.generated';
import { formatDollarAmount } from '../../../util/currency';
import { FAQTitle } from '../components/FAQComponents';
import { useShopConfig } from '../../../hooks/shopConfig';
import { useEnabledCustomerExperiences } from '../../../hooks/useEnabledCustomerExperiences';
import { useBrandCountryConfig, useUserCountryConfig } from '../../../hooks/useCountryConfig';
import { useSellerGeneratedLabels } from '../../../hooks/useSellerGeneratedLabels';

export const ShippingLengthFAQAccordionSection: FC = () => {
  const {
    copy: { faqCopy },
  } = useShopConfig();
  const { allowSell } = useEnabledCustomerExperiences();
  const { countryCode: userCountryCode, canShipWithinUserCountry } = useUserCountryConfig();
  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();
  const isSellerGeneratedLabelsEnabled = useSellerGeneratedLabels();

  let description: string;
  if (faqCopy[FAQ.ShippingLength]?.description) {
    description = faqCopy[FAQ.ShippingLength]?.description;
  } else if (!allowSell) {
    description = `After completing your order, your item can take up to ${ShipDeadline.Default} business days to ship. 
    Please note that shipping is paid for by the Buyer.`;
  } else if (isSellerGeneratedLabelsEnabled) {
    description = `After completing your order, an email is sent to the Seller, and they’re encouraged to ship immediately. 
    It usually takes around 3 days for Sellers to get items in the mail, but it can take up to ${ShipDeadline.Default} business days. 
    Please note that shipping is paid for by the Buyer.`;
  } else {
    description = `After completing your order, an email is sent to the Seller with a prepaid shipping label, and they’re encouraged to ship immediately. 
    It usually takes around 3 days for Sellers to get items in the mail, but it can take up to ${ShipDeadline.Default} business days. 
    Please note that shipping is paid for by the Buyer.`;
  }

  return (
    <StyledAccordion name="shipping-deadline">
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel2a-content"
        id="panel2a-header"
      >
        <h4>
          <FAQTitle>
            {faqCopy[FAQ.ShippingLength]?.title || 'How long will it take for my order to arrive?'}
          </FAQTitle>
        </h4>
      </AccordionSummary>
      <AccordionDetails>
        <span>
          <TypographyWrapper variant="body1">{description}</TypographyWrapper>
          {userCountryCode === CountryCode.Us && canShipWithinUserCountry && (
            <Box mt={1}>
              <TypographyWrapper variant="body1">
                {`Shipments over ${formatDollarAmount(
                  intl,
                  50000,
                  currencyConfig
                )} will require a signature confirmation upon delivery.`}
              </TypographyWrapper>
            </Box>
          )}
        </span>
      </AccordionDetails>
    </StyledAccordion>
  );
};
